import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from '@/i18n'
import { ArrowLeft, FileCode2, Loader2, Upload } from 'lucide-react'
import { toast } from 'sonner'
import AppSidebar from '@/components/AppSidebar'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { useApiFeatureFlag } from '@/hooks/useFeatureFlag'
import LadderView from './LadderView'
import StLogicView from './StLogicView'
import { fetchPlcDocument, parsePlcFile, type PlcParseResponse, type PlcPou } from './plcApi'

/**
 * Upload one IEC 61131-3 PLCopen XML export and inspect the parsed result:
 * per-POU derived coil logic (the plant's interlock/trip rules as boolean
 * expressions), declared variables, and graph size. Synchronous and
 * storage-free — nothing is indexed, the file is parsed for one response.
 */
const DROPPABLE_FILE = /\.(xml|tcpou|st|scl|l5x)$/i

const PlcParserPage = () => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [result, setResult] = useState<PlcParseResponse | null>(null)
  const [parsing, setParsing] = useState(false)
  const [dragging, setDragging] = useState(false)
  // Default DISABLED while the check is in flight: this page is a dead end for
  // flag-off tenants (the endpoint 403s), so it must not flicker in for them.
  const { enabled } = useApiFeatureFlag('plc_program_ingestion_enabled', false)
  // Catalog mode (VISN-305): /plc-parser/:docId loads the stored parse
  // artifact the plc-parsing worker persisted for that document.
  const { docId } = useParams<{ docId: string }>()

  useEffect(() => {
    if (!docId || !enabled) return
    setParsing(true)
    fetchPlcDocument(docId)
      .then((response) => setResult({ filename: '', document: response.document }))
      .catch(() =>
        toast.error(
          t('data_catalog:plc.load_failed', { defaultValue: 'Could not load the parsed program' })
        )
      )
      .finally(() => setParsing(false))
  }, [docId, enabled, t])

  const onFileSelected = async (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return
    setParsing(true)
    try {
      setResult(await parsePlcFile(file))
    } catch {
      toast.error(t('data_catalog:plc.parse_failed', { defaultValue: 'Could not parse the file' }))
    } finally {
      setParsing(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault()
    setDragging(false)
    if (parsing) return
    const file = event.dataTransfer.files?.[0]
    if (!file) return
    if (!DROPPABLE_FILE.test(file.name)) {
      toast.error(
        t('data_catalog:plc.unsupported_file', {
          defaultValue: 'Only .xml, .TcPOU, .st, .scl and .L5X files are supported',
        })
      )
      return
    }
    void onFileSelected(event.dataTransfer.files)
  }

  return (
    <div className="flex h-screen bg-background">
      <AppSidebar />
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-6 py-8">
          {docId && (
            <Link
              to="/data-catalog"
              className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('data_catalog:plc.back_to_catalog', { defaultValue: 'Back to Data Catalog' })}
            </Link>
          )}
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-foreground">
              {docId
                ? t('data_catalog:plc.viewer_title', { defaultValue: 'PLC program' })
                : t('data_catalog:plc.page_title', { defaultValue: 'PLC program parser' })}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {docId
                ? t('data_catalog:plc.viewer_subtitle', {
                    defaultValue: 'Parsed control logic, loaded from the data catalog.',
                  })
                : t('data_catalog:plc.page_subtitle', {
                    defaultValue:
                      'Upload a PLC program — PLCopen XML, Beckhoff .TcPOU, Rockwell .L5X, or IEC ST / Siemens SCL source — and inspect its logic.',
                  })}
            </p>
          </div>

          {!enabled ? (
            <div className="rounded-lg border border-border bg-muted/40 p-6 text-sm text-muted-foreground">
              {t('data_catalog:plc.disabled_notice', {
                defaultValue: 'PLC program ingestion is not enabled for this workspace.',
              })}
            </div>
          ) : (
            <div
              data-testid="plc-drop-zone"
              onDragOver={(event) => {
                if (docId) return
                event.preventDefault()
                setDragging(true)
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={docId ? undefined : onDrop}
              className={dragging ? 'rounded-lg ring-2 ring-primary/60' : undefined}
            >
              {!docId && (
                <>
                  <input
                    ref={inputRef}
                    data-testid="plc-file-input"
                    type="file"
                    accept=".xml,.TcPOU,.st,.scl,.L5X"
                    className="hidden"
                    onChange={(e) => void onFileSelected(e.target.files)}
                  />
                  <IntuiButton
                    onClick={() => inputRef.current?.click()}
                    disabled={parsing}
                    className="mb-8"
                  >
                    {parsing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('data_catalog:plc.parsing', { defaultValue: 'Parsing…' })}
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        {t('data_catalog:plc.upload_button', { defaultValue: 'Upload PLC program' })}
                      </>
                    )}
                  </IntuiButton>
                </>
              )}
              {docId && parsing && (
                <div className="mb-8 flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('data_catalog:plc.loading_document', { defaultValue: 'Loading parsed program…' })}
                </div>
              )}

              {result === null ? (
                <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                  {docId
                    ? // Viewer mode: a missing artifact means the document has no
                      // parse yet — "drag & drop" wording would be wrong here.
                      t('data_catalog:plc.viewer_empty', {
                        defaultValue: 'No parsed program is available for this document yet.',
                      })
                    : t('data_catalog:plc.empty_hint', {
                        defaultValue:
                          'Drag & drop a PLC program (.xml, .TcPOU, .st, .scl, .L5X) — the parsed result will appear here.',
                      })}
                </div>
              ) : (
                <div className="space-y-6">
                  {result.document.pous.map((pou) => (
                    <PouCard key={pou.name} pou={pou} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

const PouCard = ({ pou }: { pou: PlcPou }) => {
  const { t } = useTranslation()
  const coils = Object.entries(pou.coil_logic)
  const stRules = Object.entries(pou.st_logic)

  return (
    <section className="rounded-lg border border-border bg-card p-5">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <FileCode2 className="h-5 w-5 text-muted-foreground" />
        <h2 className="text-lg font-semibold text-foreground">{pou.name}</h2>
        <span className="rounded bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground">
          {pou.language}
        </span>
        {(pou.nodes.length > 0 || pou.edges.length > 0) && (
          <span className="ml-auto font-mono text-xs text-muted-foreground">
            {pou.nodes.length} {t('data_catalog:plc.nodes', { defaultValue: 'nodes' })} ·{' '}
            {pou.edges.length} {t('data_catalog:plc.edges', { defaultValue: 'edges' })}
          </span>
        )}
      </div>

      {pou.nodes.some((n) => n.x !== null) && (
        <>
          <h3 className="mb-2 text-sm font-medium text-foreground">
            {t('data_catalog:plc.ladder_title', { defaultValue: 'Ladder diagram' })}
          </h3>
          <div className="mb-4 overflow-x-auto rounded-md border border-border bg-muted/30 p-3">
            <LadderView pou={pou} />
          </div>
        </>
      )}

      {pou.st_rules.length > 0 && (
        <>
          <h3 className="mb-2 text-sm font-medium text-foreground">
            {t('data_catalog:plc.logic_diagram_title', { defaultValue: 'Logic diagram' })}
          </h3>
          <div className="mb-4 overflow-x-auto rounded-md border border-border bg-muted/30 p-3">
            <StLogicView rules={pou.st_rules} />
          </div>
        </>
      )}

      <h3 className="mb-2 text-sm font-medium text-foreground">
        {coils.length > 0
          ? t('data_catalog:plc.coil_logic_title', { defaultValue: 'Derived coil logic' })
          : t('data_catalog:plc.st_logic_title', { defaultValue: 'Derived logic rules' })}
      </h3>
      {coils.length === 0 && stRules.length === 0 ? (
        <p className="mb-4 text-sm text-muted-foreground">
          {t('data_catalog:plc.no_coils', { defaultValue: 'No coils found in this program.' })}
        </p>
      ) : (
        <ul className="mb-4 space-y-1 rounded-md bg-muted/50 p-3 font-mono text-sm">
          {coils.map(([coil, logic]) => (
            <li key={coil} className="text-foreground">
              <span className="font-semibold">{coil} :=</span> <span>{logic}</span>
            </li>
          ))}
          {stRules.flatMap(([variable, rules]) =>
            rules.map((rule, i) => (
              <li key={`${variable}-${i}`} className="text-foreground">
                <span className="font-semibold">{variable} :=</span> <span>{rule}</span>
              </li>
            ))
          )}
        </ul>
      )}

      {pou.variables.length > 0 && (
        <>
          <h3 className="mb-2 text-sm font-medium text-foreground">
            {t('data_catalog:plc.variables_title', { defaultValue: 'Variables' })}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase text-muted-foreground">
                  <th className="py-1.5 pr-4 font-medium">
                    {t('data_catalog:plc.col_name', { defaultValue: 'Name' })}
                  </th>
                  <th className="py-1.5 pr-4 font-medium">
                    {t('data_catalog:plc.col_type', { defaultValue: 'Type' })}
                  </th>
                  <th className="py-1.5 font-medium">
                    {t('data_catalog:plc.col_section', { defaultValue: 'Section' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {pou.variables.map((variable) => (
                  <tr key={`${variable.section}-${variable.name}`} className="border-b border-border/50">
                    <td className="py-1.5 pr-4 font-mono">{variable.name}</td>
                    <td className="py-1.5 pr-4 font-mono">{variable.type}</td>
                    <td className="py-1.5 text-muted-foreground">{variable.section}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  )
}

export default PlcParserPage
